import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Barcode, QrCode, RefreshCw } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

interface BarcodeQRCodeSectionProps {
  codeType?: 'barcode' | 'qrcode';
  codeValue?: string;
  onCodeChange: (codeType: 'barcode' | 'qrcode' | undefined, codeValue: string | undefined) => void;
  disabled?: boolean;
}

export function BarcodeQRCodeSection({
  codeType,
  codeValue,
  onCodeChange,
  disabled = false
}: BarcodeQRCodeSectionProps): React.ReactElement {
  const [selectedType, setSelectedType] = useState<'barcode' | 'qrcode' | undefined>(codeType);
  const [inputValue, setInputValue] = useState(codeValue || '');
  const barcodeCanvasRef = useRef<HTMLCanvasElement>(null);
  const qrcodeCanvasRef = useRef<HTMLCanvasElement>(null);

  // Generate random code
  const generateRandomCode = (type?: 'barcode' | 'qrcode') => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    const code = `${timestamp}${random}`;
    setInputValue(code);
    onCodeChange(type || selectedType, code);
  };

  // Render barcode
  useEffect(() => {
    if (selectedType === 'barcode' && inputValue && barcodeCanvasRef.current) {
      try {
        JsBarcode(barcodeCanvasRef.current, inputValue, {
          format: 'CODE128',
          width: 2,
          height: 80,
          displayValue: true,
          fontSize: 14,
          margin: 10,
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [selectedType, inputValue]);

  // Render QR code
  useEffect(() => {
    if (selectedType === 'qrcode' && inputValue && qrcodeCanvasRef.current) {
      QRCode.toCanvas(qrcodeCanvasRef.current, inputValue, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      }).catch((error) => {
        console.error('Error generating QR code:', error);
      });
    }
  }, [selectedType, inputValue]);

  const handleTypeChange = (type: 'barcode' | 'qrcode') => {
    setSelectedType(type);
    if (!inputValue) {
      generateRandomCode(type);
    } else {
      onCodeChange(type, inputValue);
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    onCodeChange(selectedType, value);
  };

  const handleClear = () => {
    setSelectedType(undefined);
    setInputValue('');
    onCodeChange(undefined, '');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Barcode / QR Code</CardTitle>
        <CardDescription>
          Add a barcode or QR code to this asset for tracking and label printing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Code Type Selection */}
        <div className="space-y-2">
          <Label htmlFor="codeType">Code Type</Label>
          <Select
            value={selectedType || 'none'}
            onValueChange={(value: string) => {
              if (value === 'none') {
                handleClear();
              } else {
                handleTypeChange(value as 'barcode' | 'qrcode');
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger id="codeType">
              <SelectValue placeholder="Select code type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="barcode">
                <div className="flex items-center gap-2">
                  <Barcode className="h-4 w-4" />
                  Barcode
                </div>
              </SelectItem>
              <SelectItem value="qrcode">
                <div className="flex items-center gap-2">
                  <QrCode className="h-4 w-4" />
                  QR Code
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Code Value Input */}
        {selectedType && (
          <>
            <div className="space-y-2">
              <Label htmlFor="codeValue">
                {selectedType === 'barcode' ? 'Barcode' : 'QR Code'} Value
              </Label>
              <div className="flex gap-2">
                <Input
                  id="codeValue"
                  value={inputValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleInputChange(e.target.value)}
                  placeholder={`Enter ${selectedType === 'barcode' ? 'barcode' : 'QR code'} value`}
                  disabled={disabled}
                  readOnly={disabled}
                  className={disabled ? 'bg-muted cursor-not-allowed' : ''}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => generateRandomCode()}
                  disabled={disabled}
                  title="Generate random code"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Preview */}
            {inputValue && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="border rounded-md p-4 bg-white flex items-center justify-center min-h-[120px]">
                  {selectedType === 'barcode' ? (
                    <canvas ref={barcodeCanvasRef} />
                  ) : (
                    <canvas ref={qrcodeCanvasRef} />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
